import React, { useState, useEffect, useRef, useCallback, forwardRef, useImperativeHandle, useMemo } from 'react';
import { GoogleMap, Marker, Polyline, InfoWindow, Polygon, HeatmapLayer } from '@react-google-maps/api';
import { MarkerClusterer } from '@googlemaps/markerclusterer';
import { useGoogleMaps } from '../../contexts/GoogleMapsContext';
import { FaSearch } from 'react-icons/fa';
import { IoMdRefresh } from 'react-icons/io';
import { logger } from "../../utils/logger";
import * as h3 from 'h3-js';
import DeckGLOverlay from './DeckGLOverlay';
import { CIRCUIT_CAPACITY_BUCKETS } from './circuitColorUtils';
import { FiInfo } from "react-icons/fi";
import { useParcels } from '../../contexts/ParcelContext.jsx';
import { parseAlcMultiyear } from '../../utils/capacityOutlook';
import { CircuitOutlookSection, CapacityMatrix, SUBSTATION_ROWS } from '../common/CapacityOutlookTables';

export const CHARGER_ROADMAP_STYLES = [
    { featureType: "poi", stylers: [{ visibility: "off" }] },
    { featureType: "poi.business", stylers: [{ visibility: "off" }] },
    { featureType: "poi.park", stylers: [{ visibility: "off" }] },
    { featureType: "poi.school", stylers: [{ visibility: "off" }] },
    { featureType: "poi.medical", stylers: [{ visibility: "off" }] },
    { featureType: "poi.attraction", stylers: [{ visibility: "off" }] },
    { featureType: "poi.government", stylers: [{ visibility: "off" }] },
    { featureType: "poi.sports_complex", stylers: [{ visibility: "off" }] },
    { featureType: "poi.place_of_worship", stylers: [{ visibility: "off" }] },
    { featureType: "transit", stylers: [{ visibility: "off" }] },
];

// Custom MarkerClusterer component using @googlemaps/markerclusterer
const ParcelMarkerClusterer = ({ markers, map, highlightedParcelId }) => {
    const clustererRef = useRef(null);

    useEffect(() => {
        if (!map || !markers.length) return;

        // Create custom renderer for cluster icons
        const renderer = {
            render: ({ count, position }) => {
                const label = `${count} parcels`;
                const textWidth = Math.ceil(label.length * 6.5) + 20;
                const width = Math.max(60, textWidth);
                const height = 24;
                const rx = 12;

                const svg = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(`
                    <svg width="${width}" height="${height}" viewBox="0 0 ${width} ${height}" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <rect x="1" y="1" width="${width - 2}" height="${height - 2}" rx="${rx}" fill="#ffffff" stroke="#4F46E5" stroke-width="2"/>
                        <text x="${width / 2}" y="15" text-anchor="middle" fill="#4F46E5" font-family="Arial, sans-serif" font-size="11" font-weight="bold">${label}</text>
                    </svg>
                `)}`;

                return new window.google.maps.Marker({
                    position,
                    icon: {
                        url: svg,
                        scaledSize: new window.google.maps.Size(width, height),
                        anchor: new window.google.maps.Point(width / 2, height / 2)
                    },
                    zIndex: 1000
                });
            }
        };

        // Extract the actual Google Maps markers from React Marker components
        const googleMarkers = markers.map(markerComponent => {
            const props = markerComponent.props;

            // Check if this marker represents a highlighted parcel
            const isHighlighted = highlightedParcelId && props.parcelId === highlightedParcelId;

            // Debug highlighting (can be removed in production)
            if (isHighlighted) {
                logger.log('Highlighting marker for parcel:', props.parcelId);
            }

            // Create highlighted icon if needed
            let icon = props.icon;
            if (isHighlighted && props.icon) {
                // Create a highlighted version of the icon with purple color and thicker border
                const originalIcon = props.icon;
                if (originalIcon.url) {
                    // For SVG icons, modify both stroke color and stroke width
                    let highlightedSvg = originalIcon.url
                        .replace(/#4F46E5/g, '#9333EA') // Purple color
                        .replace(/stroke-width="2"/g, 'stroke-width="4"'); // Thicker border

                    icon = {
                        ...originalIcon,
                        url: highlightedSvg
                    };
                }
            }

            const marker = new window.google.maps.Marker({
                position: props.position,
                icon: icon,
                title: props.title,
                zIndex: isHighlighted ? 1001 : 1000 // Higher z-index for highlighted markers
            });

            // Add click listener if provided
            if (props.onClick) {
                marker.addListener('click', (event) => {
                    props.onClick(event);
                });
            }

            return marker;
        });

        // Create the clusterer with proper options
        const clusterer = new MarkerClusterer({
            map,
            markers: googleMarkers,
            renderer,
            averageCenter: true,
            gridSize: 50,
            zoomOnClick: true
        });

        clustererRef.current = clusterer;

        // Cleanup function
        return () => {
            if (clustererRef.current) {
                clustererRef.current.clearMarkers();
                clustererRef.current.setMap(null);
            }
        };
    }, [map, markers, highlightedParcelId]); // highlightedParcelId is in dependencies to trigger recreation

    // This component doesn't render anything directly
    return null;
};

// Marker paths
const MARKER_PATHS = {
    depotMarker: '/img/map-markers/depot-marker.svg',
    chargerMarker: '/img/map-markers/charger-marker.svg',
    selectedDepotMarker: '/img/map-markers/depot-marker-selected.svg',
    selectedChargerMarker: '/img/map-markers/charger-marker-selected.svg',
    markerShadow: '/img/map-markers/marker-shadow.png'
};

// Info Window Content
const landParcelColumns = {
    "APN": "APN",
    "Address": "Address",
    "area": "Area (sq ft)",
    "Land_Value": "Land Value",
    "Zip5": "ZIP Code",
    "County_par": "County",
    "zone_cla_1": "Zone",
    "utility_region_name": "Utility Region",
}

// Datahub ChargingStationCalSTART fields (lon not lng)
const mhdInfrastructureColumns = {
    "name": "Name",
    "addr": "Address",
    "status": "Status",
    "type_of_access": "Access Type",
    "stall_count": "Stall Count",
    "plug_count": "Plug Count",
    "max_power": "Max Power (kW)",
    "facility_type": "Facility Type",
}

const warehouseColumns = {
    "place_name": "Name",
    "apn": "APN",
    "shape_area": "Area (sqft)",
    "category": "Category",
    "warehouse_type": "Warehouse Type",
    "county": "County",
}

const circuitColumns = {
    "circuit_name": "Circuit Name",
    "circuit_id": "Circuit ID",
    "circuit_voltage": "Voltage (kV)",
    "capacity": "Hosting Capacity (MW)",
    "substation_name": "Substation",
}

const ladwpCircuitColumns = {
    "voltage_class": "Voltage Class (kV)",
    "capacity_range_kw": "Capacity (MW)",
    "capacity_status": "Capacity Status",
    "oh_ug": "Overhead/Underground",
}

// Candidate keys for parcel fields (case-insensitive)
const parcelFieldCandidates = {
    APN: ["APN", "apn", "parcel_apn"],
    Address: ["Address", "address", "site_address"],
    Area: ["Area", "area", "area_sqft", "parcel_area_sqft", "shape_area"],
    Land_Value: ["Land_Value", "land_value", "LandValue", "landvalue"],
    Zip5: ["Zip5", "zip5", "zip", "zipcode", "zip_code", "Zip_Code", "ZIP", "postal_code", "PostalCode", "Postal_Code"],
    County_par: ["County_par", "county", "County", "county_name", "County_Name", "countyName", "CountyName"],
    zone_Juris: ["zone_Juris", "jurisdiction", "Jurisdiction", "zone_jurisdiction", "zoneJurisdiction", "zone_juris", "Zone_Juris", "jurisdiction_name"],
    zone_cla_1: ["zone_cla_1"],
    utility_region_name: ["utility_region_name", "utilityRegion", "utility", "Utility", "UtilityRegion", "utility_region_name", "Utility_Region"]
};

const circuitKeyMap = {
    "circuit_name": ["circuit_name", "Circuit_Name", "name"],
    "circuit_id": ["circuit_id", "Circuit_ID", "id"],
    "circuit_voltage": ["circuit_voltage", "voltage", "voltage_kv", "kv"],
    "capacity": ["capacity", "capacity_kw", "hosting_capacity", "capacitykw"],
    "substation_name": ["substation_name", "substation", "substation_name_full"]
};

const ladwpCircuitKeyMap = {
    "voltage_class": ["voltage_class", "Voltage_Class", "voltageClass", "voltage_kv"],
    "capacity_range_kw": ["capacity_range_kw", "capacity_kw_range", "capacityRange", "capacity_kw", "capacity"],
    "capacity_status": ["capacity_status", "Capacity_Status", "capacityStatus"],
    "oh_ug": ["oh_ug", "overhead_underground", "ohUg"]
};

const TRAFFIC_STATION_TYPE_LABELS = {
    ML: 'Mainline',
    HV: 'HOV',
    OR: 'On-ramp',
    FR: 'Off-ramp',
    CD: 'Collector/Distributor',
    FF: 'Freeway-to-Freeway',
    CH: 'Conventional Highway',
};

const truckStopColumns = {
    "name": "Name",
    "type": "Type",
    "street": "Street Address",
    "city": "City",
    "state": "State",
    "zip": "ZIP Code",
    "county": "County",
}

const utilityColumns = {
    "Utility": "Utility Name",
    "Type": "Type",
    "Phone": "Phone Number",
    "Address": "Address",
    "URL": "Website"
}

const calEnviroScreenColumns = {
    "tract": "Census Tract",
    "county": "County",
    "zip": "ZIP",
    "approx_loc": "Approximate Location",
    "tot_pop_2019": "Population (2019)",
    "ciscorep": "Percentile (0-100)",
    "pollution_burden_percentile": "Pollution Burden Percentile",
    "population_characteristics_percentile": "Population Characteristics Percentile"
}

const sraColumns = {
    "monitoring": "SRA (MONITORING)"
}

export const InfoWindowPopup = ({
    selectedContent,
    type,
}) => {
    if (!selectedContent) return null;

    const getFirstValue = (keys = []) => {
        if (!selectedContent) return undefined;
        const candidates = Array.isArray(keys) ? keys : [keys];
        const tryGet = (obj, key) => {
            if (!obj) return undefined;
            // direct
            if (obj[key] !== undefined && obj[key] !== null && obj[key] !== '') return obj[key];
            // case-insensitive
            const lowerKey = key.toString().toLowerCase();
            const match = Object.keys(obj).find(k => k.toLowerCase() === lowerKey);
            if (match && obj[match] !== undefined && obj[match] !== null && obj[match] !== '') {
                return obj[match];
            }
            return undefined;
        };

        for (const key of candidates) {
            const val = tryGet(selectedContent, key);
            if (val !== undefined) return val;
        }
        if (selectedContent.properties) {
            for (const key of candidates) {
                const val = tryGet(selectedContent.properties, key);
                if (val !== undefined) return val;
            }
        }
        return undefined;
    };

    const formatMwNumber = (n) => {
        const num = Number(n);
        if (!Number.isFinite(num)) return null;
        const s = num.toFixed(3).replace(/0+$/, '').replace(/\.$/, '');
        return s || '0';
    };

    const formatKwTextToMw = (text) => {
        if (text === null || text === undefined) return null;
        const raw = text.toString().trim();
        if (!raw) return null;
        const nums = raw.match(/(\d+(?:,\d+)?(?:\.\d+)?)/g);
        const values = (nums || [])
            .map((x) => Number(x.replace(/,/g, '')))
            .filter((v) => Number.isFinite(v));
        if (values.length === 0) return null;

        if (raw.includes('>')) {
            const low = formatMwNumber(values[0] / 1000);
            return low ? `${low}+ MW` : null;
        }
        if (raw.includes('-') && values.length >= 2) {
            const a = formatMwNumber(values[0] / 1000);
            const b = formatMwNumber(values[1] / 1000);
            return a && b ? `${a}-${b} MW` : null;
        }
        const single = formatMwNumber(values[0] / 1000);
        return single ? `${single} MW` : null;
    };

    const formatHostingCapacityMw = () => {
        const utilityRaw =
            selectedContent.utility ||
            selectedContent.Utility ||
            selectedContent.properties?.utility ||
            selectedContent.properties?.Utility ||
            '';
        const utility = utilityRaw.toString().trim().toUpperCase();

        const range = getFirstValue([
            'capacity_range_kw',
            'capacity_kw_range',
            'capacityRange',
            'capacity_range',
        ]);
        const numeric = getFirstValue([
            'capacity',
            'capacity_kw',
            'hosting_capacity',
            'capacitykw',
            'capacity_min_kw',
            'capacity_max_kw',
        ]);

        // Match regulator behavior: LADWP prefers the string range.
        if (utility === 'LADWP') {
            const mw = formatKwTextToMw(range);
            if (mw) return mw;
        }

        if (numeric !== null && numeric !== undefined && numeric !== '') {
            const n = Number(numeric);
            if (Number.isFinite(n)) {
                const mwNum = formatMwNumber(n / 1000);
                if (mwNum) return `${mwNum} MW`;
            }
            const mw = formatKwTextToMw(numeric);
            if (mw) return mw;
        }

        const mw = formatKwTextToMw(range);
        if (mw) return mw;

        return 'N/A';
    };

    const formatValue = (key, value) => {
        if (value === null || value === undefined || value === '') return 'N/A';

        // Format specific fields
        if (key === 'Land_Value') {
            const num = Number(value);
            return Number.isFinite(num) ? `$${Math.round(num).toLocaleString()}` : value;
        }
        if (key === 'area' || key === 'Area') {
            const num = Number(value);
            return Number.isFinite(num) ? `${Math.round(num).toLocaleString()} sq ft` : value;
        }
        if (key === 'shape_area') {
            const num = Number(String(value).replace(/,/g, ''));
            return Number.isFinite(num) ? Math.round(num).toLocaleString() : value;
        }
        if (key === 'Zip5' || key === 'Zip' || key === 'zipcode') {
            const num = Number(value);
            return Number.isFinite(num) ? Math.round(num).toString() : value;
        }
        if (
            key === 'ciscorep' ||
            key === 'CIscoreP' ||
            key === 'pollution_burden_percentile' ||
            key === 'population_characteristics_percentile'
        ) {
            const num = Number(value);
            return Number.isFinite(num) ? Math.round(num).toString() : value;
        }
        if (key === 'type') {
            // Format truck stop type for better display
            if (value === 'intermodal_terminal') {
                return 'Intermodal Terminal';
            } else if (value === 'service_center') {
                return 'Service Center';
            }
            return value;
        }
        if (key === 'URL' && value) {
            // Format URL as clickable link
            return (
                <a 
                    href={value} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:text-blue-800 hover:underline"
                    onClick={(e) => e.stopPropagation()}
                >
                    {value}
                </a>
            );
        }

        return value;
    };

    if (type === "landParcel") {
        return (
            <>
                {Object.keys(landParcelColumns).map((column) => {
                    const rawValue = getFirstValue(parcelFieldCandidates[column] || column);
                    const value = formatValue(column, rawValue);
                    return (
                        <div key={column}>
                            <span className="font-semibold">{landParcelColumns[column]}:</span> {value}
                        </div>
                    )
                })}
            </>
        );
    }
    else if (type === "mhdInfrastructure") {
        return (
            <>
                {Object.keys(mhdInfrastructureColumns).map((column) => {
                    const value = formatValue(column, selectedContent[column]);
                    return (
                        <div key={column}>
                            <span className="font-semibold">{mhdInfrastructureColumns[column]}:</span> {value}
                        </div>
                    )
                })}
            </>
        );
    }
    else if (type === "warehouse") {
        return (
            <>
                {Object.keys(warehouseColumns).map((column) => {
                    // For GeoJSON Features, check both properties and direct fields
                    const value = formatValue(column,
                        selectedContent.properties?.[column] || selectedContent[column]
                    );
                    return (
                        <div key={column}>
                            <span className="font-semibold">{warehouseColumns[column]}:</span> {value}
                        </div>
                    )
                })}
            </>
        );
    }
    else if (type === "circuit") {
        const utilityType = (selectedContent.utility || selectedContent.Utility || selectedContent.properties?.utility || '').toString().toLowerCase();
        const normalizedType = (selectedContent.type || selectedContent.properties?.type || '').toString().toLowerCase();
        const isLADWP = selectedContent.isLADWP || normalizedType === 'ladwp' || utilityType === 'ladwp';
        const columnsToUse = isLADWP ? ladwpCircuitColumns : circuitColumns;
        const keyMap = isLADWP ? ladwpCircuitKeyMap : circuitKeyMap;

        const utilityRaw = (
            selectedContent.utility ||
            selectedContent.Utility ||
            selectedContent.properties?.utility ||
            selectedContent.properties?.Utility ||
            'N/A'
        ).toString().trim();
        const formatSubstationCapacityMw = () => {
            const numeric = getFirstValue(['substation_capacity_mw', 'substationCapacityMw']);
            if (numeric === null || numeric === undefined || numeric === '') return 'N/A';
            const n = Number(numeric);
            if (!Number.isFinite(n)) return 'N/A';
            const mwNum = formatMwNumber(n);
            return mwNum ? `${mwNum} MW` : 'N/A';
        };

        const fieldRows = [
            { key: 'utility', label: 'Utility', value: utilityRaw ? utilityRaw.toUpperCase() : 'N/A' },
            ...Object.keys(columnsToUse).map((column) => {
                if ((!isLADWP && column === 'capacity') || (isLADWP && column === 'capacity_range_kw')) {
                    return { key: column, label: columnsToUse[column], value: formatHostingCapacityMw() };
                }
                const rawValue = getFirstValue(keyMap[column] || column);
                return { key: column, label: columnsToUse[column], value: formatValue(column, rawValue) };
            }),
        ];
        if (!isLADWP) {
            fieldRows.push({
                key: 'substation_capacity_mw',
                label: 'Substation Capacity (MW)',
                value: formatSubstationCapacityMw(),
            });
        }

        const substationOutlook = parseAlcMultiyear(getFirstValue(['substation_capacity_multiyear']));

        return (
            <>
                <table className="w-full text-xs border border-gray-200">
                    <tbody>
                        {fieldRows.map((row, idx) => (
                            <tr key={row.key} className={idx < fieldRows.length - 1 ? 'border-b border-gray-100' : ''}>
                                <td className="px-2 py-1 text-left text-gray-600 whitespace-nowrap">{row.label}</td>
                                <td className="px-2 py-1 text-right text-gray-900">{row.value ?? 'N/A'}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
                {!isLADWP && (
                    <div className="mt-2">
                        <CircuitOutlookSection
                            title="Capacity Outlook"
                            alc={parseAlcMultiyear(getFirstValue(['alc_multiyear']))}
                            emptyHint="N/A"
                        />
                        <CapacityMatrix
                            title="Substation Capacity Outlook"
                            data={substationOutlook}
                            rows={SUBSTATION_ROWS}
                            emptyHint="N/A"
                            className="mt-2"
                        />
                    </div>
                )}
            </>
        );
    }
    else if (type === "traffic") {
        const stationType = (selectedContent.station_type || 'ML').toString().trim().toUpperCase();
        const stationTypeLabel = TRAFFIC_STATION_TYPE_LABELS[stationType] || stationType;
        const truckVolume = selectedContent.truck_volume ?? selectedContent.volume ?? null;
        const vehicleVolume = selectedContent.vehicle_volume ?? null;
        const isRamp = stationType === 'OR' || stationType === 'FR';

        return (
            <>
                <div>
                    <span className="font-semibold">Station type:</span> {stationTypeLabel}
                </div>
                <div>
                    <span className="font-semibold">Freeway:</span> {formatValue('freeway', selectedContent.freeway)}
                </div>
                <div>
                    <span className="font-semibold">Direction:</span> {formatValue('direction', selectedContent.direction)}
                </div>
                {!isRamp && truckVolume != null && Number.isFinite(Number(truckVolume)) ? (
                    <div>
                        <span className="font-semibold">Truck volume:</span>{' '}
                        {Math.round(Number(truckVolume)).toLocaleString()} trucks/day
                    </div>
                ) : null}
                {isRamp && vehicleVolume != null && Number.isFinite(Number(vehicleVolume)) ? (
                    <div>
                        <span className="font-semibold">Vehicle volume:</span>{' '}
                        {Math.round(Number(vehicleVolume)).toLocaleString()} vehicles/day
                    </div>
                ) : null}
            </>
        );
    }
    else if (type === "truckStop") {
        return (
            <>
                {Object.keys(truckStopColumns).map((column) => {
                    const value = formatValue(column, selectedContent[column]);
                    return (
                        <div key={column}>
                            <span className="font-semibold">{truckStopColumns[column]}:</span> {value}
                        </div>
                    )
                })}
            </>
        );
    }
    else if (type === "utility") {
        // Debug: log the selected content
        logger.log('Utility selectedContent:', selectedContent);
        logger.log('Utility keys:', Object.keys(selectedContent));
        
        return (
            <>
                {Object.keys(utilityColumns).map((column) => {
                    const value = formatValue(column, selectedContent[column]);
                    logger.log(`Utility column ${column}:`, selectedContent[column], '-> formatted:', value);
                    return (
                        <div key={column}>
                            <span className="font-semibold">{utilityColumns[column]}:</span> {value}
                        </div>
                    )
                })}
            </>
        );
    }
    else if (type === "calEnviroScreen") {
        return (
            <>
                {Object.keys(calEnviroScreenColumns).map((column) => {
                    const value = formatValue(column, getFirstValue(column));
                    return (
                        <div key={column}>
                            <span className="font-semibold">{calEnviroScreenColumns[column]}:</span> {value}
                        </div>
                    )
                })}
            </>
        );
    }
    else if (type === "sra") {
        return (
            <>
                {Object.keys(sraColumns).map((column) => {
                    const value = formatValue(column, getFirstValue(column));
                    return (
                        <div key={column}>
                            <span className="font-semibold">{sraColumns[column]}:</span> {value}
                        </div>
                    )
                })}
            </>
        );
    }

    // Default case - return null if type doesn't match
    return null;
}

// NOTE: WKT parsing function - no longer used since server sends GeoJSON
// Kept for potential fallback if needed
const parseWKTLineString = (wktString) => {
    try {
        logger.log("=== parseWKTLineString Debug ===");
        logger.log("Input WKT string:", wktString);
        logger.log("WKT string type:", typeof wktString);
        logger.log("WKT string length:", wktString ? wktString.length : 'null');

        if (!wktString) {
            logger.warn("WKT string is null or undefined");
            return null;
        }

        // Remove SRID prefix and extract coordinates
        const cleanWkt = wktString.replace(/^SRID=\d+;/, '').trim();
        logger.log("Cleaned WKT (SRID removed):", cleanWkt.substring(0, 80) + (cleanWkt.length > 80 ? '...' : ''));

        // MULTILINESTRING handling: return array of paths
        if (/^MULTILINESTRING/i.test(cleanWkt)) {
            logger.log('Parsing MULTILINESTRING WKT');
            // Strip leading MULTILINESTRING( and trailing )
            let content = cleanWkt
                .replace(/^MULTILINESTRING\s*\(\s*/i, '')
                .replace(/\s*\)\s*$/i, '');

            // Split into individual linestring blocks by '), ('
            const linestringBlocks = content.split(/\)\s*,\s*\(/);
            logger.log(`Found ${linestringBlocks.length} linestring blocks in MULTILINESTRING`);

            const paths = linestringBlocks.map((block, idx) => {
                // Remove any leading/trailing parens that might remain
                let lineContent = block.replace(/^\(/, '').replace(/\)$/, '');

                const coords = lineContent.split(',').map(coord => {
                    const [xStr, yStr] = coord.trim().split(/\s+/);
                    const x = Number(xStr);
                    const y = Number(yStr);
                    if (isNaN(x) || isNaN(y)) return null;
                    if (Math.abs(x) > 180 || Math.abs(y) > 90) {
                        const lng = (x / 20037508.34) * 180;
                        const lat = (Math.atan(Math.exp((y / 20037508.34) * Math.PI)) * 360 / Math.PI) - 90;
                        return { lat, lng };
                    }
                    return { lat: y, lng: x };
                }).filter(Boolean);
                logger.log(`Linestring ${idx + 1} has ${coords.length} points`);
                return coords.length ? coords : null;
            }).filter(Boolean);

            if (!paths.length) {
                logger.warn('No valid paths parsed from MULTILINESTRING');
                return null;
            }
            logger.log(`Returning ${paths.length} paths from MULTILINESTRING`);
            return paths; // Array of paths
        }

        // LINESTRING handling: return single path
        if (/^LINESTRING/i.test(cleanWkt)) {
            logger.log('Parsing single LINESTRING WKT');
            // Extract the inside of the parentheses
            const match = cleanWkt.match(/LINESTRING\s*\((.+?)\)/i);
            if (!match) {
                logger.warn('LINESTRING content not found');
                return null;
            }

            const coordinates = match[1].split(',').map(coord => {
                const [xStr, yStr] = coord.trim().split(/\s+/);
                const x = Number(xStr);
                const y = Number(yStr);
                if (isNaN(x) || isNaN(y)) return null;
                if (Math.abs(x) > 180 || Math.abs(y) > 90) {
                    const lng = (x / 20037508.34) * 180;
                    const lat = (Math.atan(Math.exp((y / 20037508.34) * Math.PI)) * 360 / Math.PI) - 90;
                    return { lat, lng };
                }
                return { lat: y, lng: x };
            }).filter(Boolean);
            logger.log("Returning single linestring with", coordinates.length, "points");
            return coordinates; // Single path
        }

        logger.warn('No valid LINESTRING or MULTILINESTRING found in WKT');
        return null;

    } catch (error) {
        logger.error('Error parsing WKT linestring:', error);
        logger.error('WKT string:', wktString);
        return null;
    }
};

// Function to parse WKT polygon geometry to Google Maps format
const parseWKTPolygon = (wktString) => {
    try {
        logger.log("=== parseWKTPolygon Debug ===");
        logger.log("Input WKT string:", wktString);
        logger.log("WKT string type:", typeof wktString);
        logger.log("WKT string length:", wktString ? wktString.length : 'null');

        if (!wktString) {
            logger.warn("WKT string is null or undefined");
            return null;
        }

        // Remove SRID prefix and extract coordinates
        const cleanWkt = wktString.replace(/^SRID=\d+;/, '').trim();
        logger.log("Cleaned WKT (SRID removed):", cleanWkt.substring(0, 80) + (cleanWkt.length > 80 ? '...' : ''));

        // MULTIPOLYGON handling: return array of polygons (outer rings only)
        if (/^MULTIPOLYGON/i.test(cleanWkt)) {
            logger.log('Parsing MULTIPOLYGON WKT');
            // Strip leading MULTIPOLYGON( and trailing )
            let content = cleanWkt
                .replace(/^MULTIPOLYGON\s*\(\s*/i, '')
                .replace(/\s*\)\s*$/i, '');

            // Split into individual polygon blocks by ')), (('
            const polygonBlocks = content.split(/\)\)\s*,\s*\(\(/);
            logger.log(`Found ${polygonBlocks.length} polygon blocks in MULTIPOLYGON`);

            const polygons = polygonBlocks.map((block, idx) => {
                // Remove any leading/trailing parens that might remain
                let polyContent = block.replace(/^\(\(/, '').replace(/\)\)$/, '');
                // Each polygon may have multiple rings: split by '), (' and take the first ring (outer boundary)
                const rings = polyContent.split(/\)\s*,\s*\(/);
                const outerRing = rings[0];
                if (!outerRing) return null;

                const coords = outerRing.split(',').map(coord => {
                    const [xStr, yStr] = coord.trim().split(/\s+/);
                    const x = Number(xStr);
                    const y = Number(yStr);
                    if (isNaN(x) || isNaN(y)) return null;
                    if (Math.abs(x) > 180 || Math.abs(y) > 90) {
                        const lng = (x / 20037508.34) * 180;
                        const lat = (Math.atan(Math.exp((y / 20037508.34) * Math.PI)) * 360 / Math.PI) - 90;
                        return { lat, lng };
                    }
                    return { lat: y, lng: x };
                }).filter(Boolean);
                logger.log(`Polygon ${idx + 1} outer ring has ${coords.length} points`);
                return coords.length ? coords : null;
            }).filter(Boolean);

            if (!polygons.length) {
                logger.warn('No valid polygons parsed from MULTIPOLYGON');
                return null;
            }
            logger.log(`Returning ${polygons.length} polygons from MULTIPOLYGON`);
            return polygons; // Array of paths
        }

        // POLYGON handling: return single polygon path (outer ring only)
        if (/^POLYGON/i.test(cleanWkt)) {
            logger.log('Parsing single POLYGON WKT');
            // Extract the inside of the first pair of double parentheses (( ... ))
            const match = cleanWkt.match(/POLYGON\s*\(\((.+?)\)\)/i);
            if (!match) {
                logger.warn('POLYGON content not found');
                return null;
            }
            // Take only the outer ring (before any ") , (" separation)
            const outerRing = match[1].split(/\)\s*,\s*\(/)[0];
            const coordinates = outerRing.split(',').map(coord => {
                const [xStr, yStr] = coord.trim().split(/\s+/);
                const x = Number(xStr);
                const y = Number(yStr);
                if (isNaN(x) || isNaN(y)) return null;
                if (Math.abs(x) > 180 || Math.abs(y) > 90) {
                    const lng = (x / 20037508.34) * 180;
                    const lat = (Math.atan(Math.exp((y / 20037508.34) * Math.PI)) * 360 / Math.PI) - 90;
                    return { lat, lng };
                }
                return { lat: y, lng: x };
            }).filter(Boolean);
            logger.log("Returning single polygon with", coordinates.length, "points");
            return coordinates; // Single path
        }

        logger.warn('No valid POLYGON or MULTIPOLYGON found in WKT');
        return null;

    } catch (error) {
        logger.error('Error parsing WKT polygon:', error);
        logger.error('WKT string:', wktString);
        return null;
    }
};

// ==== Marker Icons ====
const createMarkerIcons = () => {
    if (!window.google) return null;

    return {
        charger: {
            default: {
                url: MARKER_PATHS.chargerMarker,
                scaledSize: new window.google.maps.Size(30, 49),
                anchor: new window.google.maps.Point(15, 38),
                labelOrigin: new window.google.maps.Point(15, -10),
                shadow: {
                    url: MARKER_PATHS.markerShadow,
                    scaledSize: new window.google.maps.Size(49, 49),
                    anchor: new window.google.maps.Point(15, 49)
                }
            },
            selected: {
                url: MARKER_PATHS.selectedChargerMarker,
                scaledSize: new window.google.maps.Size(30, 49),
                anchor: new window.google.maps.Point(15, 38),
                labelOrigin: new window.google.maps.Point(15, -10),
                shadow: {
                    url: MARKER_PATHS.markerShadow,
                    scaledSize: new window.google.maps.Size(49, 49),
                    anchor: new window.google.maps.Point(15, 49)
                }
            }
        },
        depot: {
            default: {
                url: MARKER_PATHS.depotMarker,
                scaledSize: new window.google.maps.Size(45, 73),
                anchor: new window.google.maps.Point(22, 58),
                labelOrigin: new window.google.maps.Point(22, -15),
                shadow: {
                    url: MARKER_PATHS.markerShadow,
                    scaledSize: new window.google.maps.Size(73, 73),
                    anchor: new window.google.maps.Point(22, 73)
                }
            },
            selected: {
                url: MARKER_PATHS.selectedDepotMarker,
                scaledSize: new window.google.maps.Size(45, 73),
                anchor: new window.google.maps.Point(22, 58),
                labelOrigin: new window.google.maps.Point(22, -15),
                shadow: {
                    url: MARKER_PATHS.markerShadow,
                    scaledSize: new window.google.maps.Size(73, 73),
                    anchor: new window.google.maps.Point(22, 73)
                }
            }
        }
    };
};

// Map components
export const ChargerGoogleMap = forwardRef(({
    landParcelData = [],
    topParcelPoints = [],
    scaqmdJurisdiction = [],
    sraData = [],
    mhdInfrastructure = [],
    warehouses = [],
    handleSearchInArea,
    onBoundsChange,
    onParcelClick,
    heatmapData,
    disadvantagedCommunities,
    calEnviroScreen = [],
    truckStops,
    circuitData = [],
    circuitContours = [],
    currentZoom: parentZoom,
    trafficData = [],
    trafficStationFilters = { mainline: true, onramp: true, offramp: true },
    utilitiesData = [],
    loading = false,
    highlightedParcelId = null,
    updateHighlightedParcel,
    parcelFilters = {},
    searchId = null,
    parcelPointTileUrl = null,
    parcelPolygonTileUrl = null,
    landParcelCheckbox = true,
    SCAQMDCheckbox = false,
    sraCheckbox = false,
    mhdInfrastructureCheckbox = false,
    warehouseCheckbox = false,
    truckStopCheckbox = false,
    intermodalTerminalCheckbox = false,
    serviceCenterCheckbox = false,
    truckParkingLocationCheckbox = false,
    pemsTrafficCheckbox = false,
    disadvantagedCommunitiesCheckbox = false,
    calEnviroScreenCheckbox = false,
    circuitCapacityCheckbox = false,
    utilitiesCheckbox = false
}, ref) => {
    const { isLoaded, loadError } = useGoogleMaps();
    const [markerIcons, setMarkerIcons] = useState(null);
    const [isGoogleMapsLoaded, setIsGoogleMapsLoaded] = useState(false);
    const { selectedParcels, setSelectedParcels, noteParcelEngagement } = useParcels();
    const [selectedParcel, setSelectedParcel] = useState(null);
    const [infoWindowPosition, setInfoWindowPosition] = useState(null);
    const [selectedContentType, setSelectedContentType] = useState(null);
    const [placeQuery, setPlaceQuery] = useState('');
    const [placeResults, setPlaceResults] = useState([]);
    const [selectedPlace, setSelectedPlace] = useState(null);
    const mapRef = useRef(null);
    const boundsChangeTimeoutRef = useRef(null);
    const placesServiceRef = useRef(null);
    const autocompleteInputRef = useRef(null);
    const [currentZoom, setCurrentZoom] = useState(10);
    const isInitialLoadRef = useRef(true);
    const autocompleteInstanceRef = useRef(null);
    const autocompleteStyleInjectedRef = useRef(false);

    const [activeLegendTab, setActiveLegendTab] = useState('circuits'); // 'circuits', 'traffic', 'truckHeatmap'

    const handlePlacesSearch = useCallback(() => {
        if (!placesServiceRef.current || !mapRef.current) return;
        const query = (placeQuery || '').trim();
        if (!query) return;

        const bounds = mapRef.current.getBounds();
        const request = {
            query,
            bounds: bounds || undefined,
            region: 'us'
        };

        placesServiceRef.current.textSearch(request, (results, status) => {
            if (status !== window.google.maps.places.PlacesServiceStatus.OK || !results) {
                logger.warn('Places search returned no results', status);
                setPlaceResults([]);
                setSelectedPlace(null);
                return;
            }

            const mapped = results.map(r => ({
                id: r.place_id,
                name: r.name || r.formatted_address || 'Place',
                position: { lat: r.geometry?.location?.lat(), lng: r.geometry?.location?.lng() },
                address: r.formatted_address || '',
                rating: r.rating,
                businessStatus: r.business_status
            })).filter(p => p.position?.lat && p.position?.lng);

            setPlaceResults(mapped);
            setSelectedPlace(null);

            if (mapped.length > 0) {
                const fitBounds = new window.google.maps.LatLngBounds();
                mapped.forEach(p => fitBounds.extend(p.position));
                mapRef.current.fitBounds(fitBounds);
            }
        });
    }, [placeQuery]);

    // Initialize Google Places Autocomplete on the input field
    useEffect(() => {
        if (!isLoaded || !autocompleteInputRef.current || autocompleteInstanceRef.current) return;
        if (!window.google?.maps?.places?.Autocomplete) return;

        if (!autocompleteStyleInjectedRef.current) {
            const styleTag = document.createElement('style');
            styleTag.id = 'pac-custom-style';
            styleTag.innerHTML = `
                .pac-container, .pac-item, .pac-item-query {
                    font-family: 'Nunito', 'Inter', system-ui, -apple-system, sans-serif !important;
                }
            `;
            document.head.appendChild(styleTag);
            autocompleteStyleInjectedRef.current = true;
        }

        // Derive bounds from current map center if available, otherwise fallback sample
        const mapCenter = mapRef.current?.getCenter?.();
        const centerLat = mapCenter?.lat?.();
        const centerLng = mapCenter?.lng?.();
        const baseCenter = Number.isFinite(centerLat) && Number.isFinite(centerLng)
            ? { lat: centerLat, lng: centerLng }
            : { lat: 50.064192, lng: -130.605469 };

        const defaultBounds = {
            north: baseCenter.lat + 0.1,
            south: baseCenter.lat - 0.1,
            east: baseCenter.lng + 0.1,
            west: baseCenter.lng - 0.1,
        };

        const options = {
            bounds: defaultBounds,
            componentRestrictions: { country: 'us' },
            strictBounds: false,
        };

        const ac = new window.google.maps.places.Autocomplete(autocompleteInputRef.current, options);
        autocompleteInstanceRef.current = ac;

        const listener = ac.addListener('place_changed', () => {
            const place = ac.getPlace();
            const address = place.formatted_address || place.name || '';
            setPlaceQuery(address);
            setPlaceResults([]);
            setSelectedPlace(null);
            const loc = place.geometry?.location;
            if (loc && mapRef.current) {
                const lat = loc.lat();
                const lng = loc.lng();
                if (Number.isFinite(lat) && Number.isFinite(lng)) {
                    mapRef.current.panTo({ lat, lng });
                    const nextZoom = Math.max(15, mapRef.current.getZoom?.() ?? 15);
                    mapRef.current.setZoom?.(nextZoom);
                }
            }
        });

        return () => {
            if (listener?.remove) listener.remove();
            autocompleteInstanceRef.current = null;
        };
    }, [isLoaded]);

    // Expose map methods via ref
    useImperativeHandle(ref, () => ({
        getMap: () => mapRef.current,
        getBounds: () => {
            if (mapRef.current) {
                const bounds = mapRef.current.getBounds();
                if (bounds) {
                    const ne = bounds.getNorthEast();
                    const sw = bounds.getSouthWest();
                    return {
                        north: ne.lat(),
                        south: sw.lat(),
                        east: ne.lng(),
                        west: sw.lng()
                    };
                }
            }
            return null;
        },
        panToParcel: (parcel) => {
            if (mapRef.current && parcel) {
                const lat = parseFloat(parcel.Center_Lat);
                const lng = parseFloat(parcel.Center_Lon);

                if (!isNaN(lat) && !isNaN(lng)) {
                    const position = { lat, lng };
                    mapRef.current.panTo(position);
                    mapRef.current.setZoom(Math.max(15, mapRef.current.getZoom())); // Ensure high enough zoom to see parcel
                    logger.log('Panned to parcel:', { lat, lng, address: parcel.Address });
                } else {
                    logger.warn('Invalid parcel coordinates for panning:', parcel);
                }
            }
        },
        restorePosition: (center, zoom) => {
            if (mapRef.current && center) {
                mapRef.current.panTo(center);
                if (typeof zoom === 'number') {
                    mapRef.current.setZoom(zoom);
                }
            }
        }
    }));

    const onLoad = useCallback((map) => {
        logger.log('Map loaded successfully');
        mapRef.current = map;
        setIsGoogleMapsLoaded(true);
        const icons = createMarkerIcons();
        setMarkerIcons(icons);
        try {
            if (window.google?.maps?.places) {
                placesServiceRef.current = new window.google.maps.places.PlacesService(map);
                logger.log('PlacesService initialized');
            }
        } catch (error) {
            logger.error('Failed to init PlacesService:', error);
        }

        // Set initial position only once
        if (isInitialLoadRef.current) {
            map.setCenter({ lat: 33.9, lng: -117.5 });
            map.setZoom(10);
            isInitialLoadRef.current = false;
        }

        // Initial bounds change after a short delay to ensure map is fully loaded
        setTimeout(() => {
            if (onBoundsChange) {
                const bounds = map.getBounds();
                const zoom = map.getZoom();
                setCurrentZoom(zoom);

                if (bounds) {
                    const ne = bounds.getNorthEast();
                    const sw = bounds.getSouthWest();
                    const boundsObj = {
                        north: ne.lat(),
                        south: sw.lat(),
                        east: ne.lng(),
                        west: sw.lng()
                    };
                    logger.log('Initial bounds:', boundsObj, 'Zoom:', zoom);
                    onBoundsChange(boundsObj, zoom);
                }
            }
        }, 100);
    }, [onBoundsChange]);

    // Debounced bounds change handler
    const handleBoundsChanged = useCallback(() => {
        if (!mapRef.current || !onBoundsChange) return;

        if (boundsChangeTimeoutRef.current) {
            clearTimeout(boundsChangeTimeoutRef.current);
        }

        boundsChangeTimeoutRef.current = setTimeout(() => {
            const bounds = mapRef.current.getBounds();
            const zoom = mapRef.current.getZoom();
            setCurrentZoom(zoom);

            if (bounds) {
                const ne = bounds.getNorthEast();
                const sw = bounds.getSouthWest();
                const boundsObj = {
                    north: ne.lat(),
                    south: sw.lat(),
                    east: ne.lng(),
                    west: sw.lng()
                };
                logger.log('Bounds changed:', boundsObj, 'Zoom:', zoom);
                onBoundsChange(boundsObj, zoom);
            }
        }, 300); // 300ms debounce
    }, [onBoundsChange]);

    const onError = (error) => {
        logger.error('Google Maps failed to load:', error);
    };

    // Handle polygon/polyline click from DeckGL
    const handleDeckGLClick = useCallback((object, event) => {
        setSelectedParcel(object);

        // Get click position from deck.gl event
        const { coordinate } = event;
        if (coordinate) {
            setInfoWindowPosition({
                lat: coordinate[1],
                lng: coordinate[0]
            });
        }

        // Determine content type based on the data structure
        if (object.type === 'regular' || object.type === 'ladwp' ||
            (object.type === 'Feature' && object.geometry && (object.geometry.type === 'LineString' || object.geometry.type === 'MultiLineString'))) {
            // Circuit data (either our custom type or GeoJSON Feature with LineString)
            setSelectedContentType('circuit');
        } else if (object.place_name || object.warehouse_class ||
            (object.type === 'Feature' && object.geometry && (object.geometry.type === 'Polygon' || object.geometry.type === 'MultiPolygon')) ||
            (object.properties && (object.properties.place_name || object.properties.warehouse_class))) {
            setSelectedContentType('warehouse');
        } else if (object.APN || object.Address || object.Land_Value ||
            object.properties?.APN || object.properties?.Address || object.properties?.Land_Value ||
            object.properties?.apn || object.properties?.address || object.properties?.land_value) {
            // Land parcel data structure (handles both direct fields and GeoJSON properties)
            setSelectedContentType('landParcel');
            // Call the parcel click handler if provided
            if (onParcelClick) {
                onParcelClick(object);
            }
        } else {
            // Default to land parcel for unknown structures
            setSelectedContentType('landParcel');
            // Call the parcel click handler if provided
            if (onParcelClick) {
                onParcelClick(object);
            }
        }
    }, [onParcelClick]);

    // Handle MHD infrastructure marker click from DeckGL
    const handleMhdMarkerClick = useCallback((infrastructure, event) => {
        setSelectedParcel(infrastructure);

        // Get click position from deck.gl event or use infrastructure coordinates
        const { coordinate } = event;
        if (coordinate) {
            setInfoWindowPosition({
                lat: coordinate[1],
                lng: coordinate[0]
            });
        } else {
            setInfoWindowPosition({
                lat: parseFloat(infrastructure.lat),
                lng: parseFloat(infrastructure.lon)
            });
        }
        setSelectedContentType('mhdInfrastructure');
    }, []);

    // Handle truck stop click from DeckGL
    const handleTruckStopClick = useCallback((stop, event) => {
        setSelectedParcel(stop);

        // Get click position from deck.gl event or use stop coordinates
        const { coordinate } = event;
        if (coordinate) {
            setInfoWindowPosition({
                lat: coordinate[1],
                lng: coordinate[0]
            });
        } else {
            setInfoWindowPosition({
                lat: parseFloat(stop.latitude),
                lng: parseFloat(stop.longitude)
            });
        }
        setSelectedContentType('truckStop');
    }, []);

    // Handle traffic click from DeckGL
    const handleTrafficClick = useCallback((traffic, event) => {
        setSelectedParcel(traffic);

        // Get click position from deck.gl event or use traffic coordinates
        const { coordinate } = event;
        if (coordinate) {
            setInfoWindowPosition({
                lat: coordinate[1],
                lng: coordinate[0]
            });
        } else {
            // Parse coordinates from SRID=4326;POINT format
            const locationMatch = traffic.location?.match(/POINT\s*\(\s*([^)]+)\s*\)/);
            if (locationMatch) {
                const [lng, lat] = locationMatch[1].split(/\s+/).map(Number);
                if (!isNaN(lat) && !isNaN(lng)) {
                    setInfoWindowPosition({ lat, lng });
                }
            }
        }
        setSelectedContentType('traffic');
    }, []);

    // Handle circuit click from DeckGL
    const handleCircuitClick = useCallback((circuit, coordinate) => {
        logger.log('=== HANDLE CIRCUIT CLICK ===');
        logger.log('Circuit data received:', circuit);
        logger.log('Circuit type:', typeof circuit);
        logger.log('Circuit keys:', Object.keys(circuit || {}));
        logger.log('Circuit values:', Object.entries(circuit || {}));
        logger.log('Coordinate:', coordinate);
        
        setSelectedParcel(circuit);

        // Get click position from deck.gl event
        if (coordinate && coordinate.length >= 2) {
            setInfoWindowPosition({
                lat: coordinate[1],
                lng: coordinate[0]
            });
        }
        setSelectedContentType('circuit');
        
        logger.log('Set selectedContentType to: circuit');
        logger.log('Set selectedParcel to:', circuit);
    }, []);

    // Handle warehouse click from DeckGL
    const handleWarehouseClick = useCallback((warehouse, coordinate) => {
        setSelectedParcel(warehouse);

        // Get click position from deck.gl event
        if (coordinate && coordinate.length >= 2) {
            setInfoWindowPosition({
                lat: coordinate[1],
                lng: coordinate[0]
            });
        }
        setSelectedContentType('warehouse');
    }, []);

    const handleUtilityClick = useCallback((utility, coordinate) => {
        setSelectedParcel(utility);

        // Get click position from deck.gl event
        if (coordinate && coordinate.length >= 2) {
            setInfoWindowPosition({
                lat: coordinate[1],
                lng: coordinate[0]
            });
        }
        setSelectedContentType('utility');
    }, []);

    const handleCalEnviroScreenClick = useCallback((tract, coordinate) => {
        setSelectedParcel(tract);
        if (coordinate && coordinate.length >= 2) {
            setInfoWindowPosition({
                lat: coordinate[1],
                lng: coordinate[0]
            });
        }
        setSelectedContentType('calEnviroScreen');
    }, []);

    const handleSraClick = useCallback((sra, coordinate) => {
        setSelectedParcel(sra);
        if (coordinate && coordinate.length >= 2) {
            setInfoWindowPosition({
                lat: coordinate[1],
                lng: coordinate[0]
            });
        }
        setSelectedContentType('sra');
    }, []);

    // Close info window
    const closeInfoWindow = useCallback(() => {
        setSelectedParcel(null);
        setInfoWindowPosition(null);
        setSelectedContentType(null);
    }, []);

    // Helper function to get parcel key (same as in ParcelContext)
    const getParcelKey = useCallback((parcel) => {
        if (!parcel) return undefined;
        return parcel.id ?? `${parcel.APN ?? ""}-${parcel.Zip5 ?? parcel.Zip ?? ""}-${parcel.Address ?? ""}`;
    }, []);

    // Add selected parcel to table (memory only, no navigation)
    const handleAddParcelToTable = useCallback(() => {
        if (!selectedParcel) return;
        const key = getParcelKey(selectedParcel);
        logger.log(`Adding parcel ${key} to table from map`);
        if (!key) return;
        const alreadySelected = selectedParcels?.some(p => getParcelKey(p) === key);
        if (alreadySelected) return;
        setSelectedParcels(prev => {
            const existingKeys = new Set(prev.map(p => getParcelKey(p)));
            if (existingKeys.has(key)) return prev;
            return [...prev, selectedParcel];
        });
        noteParcelEngagement?.(selectedParcel, "added_to_table");
    }, [selectedParcel, selectedParcels, getParcelKey, setSelectedParcels, noteParcelEngagement]);

    const parcelAlreadyInTable = useMemo(() => {
        if (!selectedParcel) return false;
        const key = getParcelKey(selectedParcel);
        if (!key) return false;
        return selectedParcels?.some(p => getParcelKey(p) === key);
    }, [selectedParcel, selectedParcels, getParcelKey]);

    // Handle polygon click for parcels
    const handlePolygonClick = useCallback((parcel, event) => {
        setSelectedParcel(parcel);
        setSelectedContentType('landParcel');

        // Update highlighted parcel
        if (updateHighlightedParcel) {
            const parcelKey = getParcelKey(parcel);
            updateHighlightedParcel(parcelKey);
        }

        // Get click position from event
        if (event && event.latLng) {
            setInfoWindowPosition({
                lat: event.latLng.lat(),
                lng: event.latLng.lng()
            });
        } else if (parcel.Center_Lat && parcel.Center_Lon) {
            // Fallback for old marker system
            setInfoWindowPosition({
                lat: parseFloat(parcel.Center_Lat),
                lng: parseFloat(parcel.Center_Lon)
            });
        } else {
            // Last resort: use map center or don't show info window
            logger.warn('No position available for parcel click, using map center');
            if (mapRef.current) {
                const center = mapRef.current.getCenter();
                if (center) {
                    setInfoWindowPosition({
                        lat: center.lat(),
                        lng: center.lng()
                    });
                }
            }
        }

        // Call the parent's onParcelClick if provided
        if (onParcelClick) {
            onParcelClick(parcel);
        }
    }, [onParcelClick, updateHighlightedParcel, getParcelKey]);

    // Get color for land parcel based on value
    const getLandParcelColor = (value) => {
        if (value > 2000000) return [220, 38, 127]; // Pink
        if (value > 1000000) return [245, 158, 11]; // Orange
        if (value > 500000) return [245, 158, 11]; // Orange
        if (value > 100000) return [16, 185, 129]; // Green
        return [79, 70, 229]; // Blue
    };


    // Memoize parcel rendering for performance
    // OLD: Parcel rendering with Google Maps markers - REMOVED
    // Now using MVT tiles via DeckGL for parcel polygons
    // No client-side clustering or marker rendering needed
    const renderedParcels = null;

    const renderedHeatmap = React.useMemo(() => {
        const getColorByWeight = (weight) => {
            if (weight > 1000) return "#800026";
            if (weight > 500) return "#BD0026";
            if (weight > 200) return "#E31A1C";
            if (weight > 100) return "#FC4E2A";
            if (weight > 50) return "#FD8D3C";
            if (weight > 20) return "#FEB24C";
            if (weight > 10) return "#FED976";
            return "#FFEDA0";
        }

        if (
            !truckParkingLocationCheckbox ||
            !Array.isArray(heatmapData) ||
            heatmapData.length === 0
        ) {
            logger.log("Heatmap data is not an array or is empty:", heatmapData);
            return null;
        }

        return heatmapData.map((hex, i) => {
            if (!hex.h3_index) {
                logger.log("Hex is missing h3_index:", hex);
                return null;
            }

            const boundary = h3.cellToBoundary(hex.h3_index, true); // [[lng, lat]] when true
            const path = boundary.map(([lng, lat]) => ({ lat, lng }));

            const w = Number(hex.weight ?? hex.point_count ?? 0);

            return (
                <Polygon
                    key={hex.h3_index || i}
                    paths={path}
                    options={{
                        fillColor: getColorByWeight(w),
                        fillOpacity: 0.5,
                        strokeColor: "#FF0000",
                        strokeOpacity: 0.5,
                        strokeWeight: 1,
                        clickable: false,
                        zIndex: 10,
                    }}
                />
            );
        });
    }, [heatmapData, truckParkingLocationCheckbox]);

    if (loadError) {
        logger.error('Google Maps load error:', loadError);
        return <div className="flex items-center justify-center h-[75vh] text-red-600">Error loading Google Maps: {loadError.message}</div>;
    }

    if (!isLoaded) {
        return <div className="flex items-center justify-center h-[75vh] text-gray-600">Loading Google Maps...</div>;
    }

    return (
        <div className="flex flex-col h-full">
        <div className="relative flex-1 min-h-0">
            <GoogleMap
                mapContainerStyle={{ width: '100%', height: '100%' }}
                onLoad={onLoad}
                onBoundsChanged={handleBoundsChanged}
                onClick={closeInfoWindow}
                options={{
                    disableDefaultUI: false,
                    zoomControl: true,
                    streetViewControl: false,
                    mapTypeControl: true,
                    fullscreenControl: true,
                    gestureHandling: 'greedy',
                    styles: CHARGER_ROADMAP_STYLES
                }}
            >
                {/* DeckGL Overlay for all polygon and marker layers */}
                {mapRef.current && (
                    <DeckGLOverlay
                        map={mapRef.current}
                        scaqmdJurisdiction={scaqmdJurisdiction}
                        sraData={sraData}
                        mhdInfrastructure={mhdInfrastructure}
                        warehouses={warehouses}
                        disadvantagedCommunities={disadvantagedCommunities}
                        calEnviroScreen={calEnviroScreen}
                        truckStops={truckStops}
                        circuitData={circuitData}
                        circuitContours={circuitContours}
                        heatmapData={heatmapData}
                        trafficData={trafficData}
                        trafficStationFilters={trafficStationFilters}
                        utilitiesData={utilitiesData}
                        SCAQMDCheckbox={SCAQMDCheckbox}
                        sraCheckbox={sraCheckbox}
                        mhdInfrastructureCheckbox={mhdInfrastructureCheckbox}
                        warehouseCheckbox={warehouseCheckbox}
                        disadvantagedCommunitiesCheckbox={disadvantagedCommunitiesCheckbox}
                        calEnviroScreenCheckbox={calEnviroScreenCheckbox}
                        truckStopCheckbox={truckStopCheckbox}
                        intermodalTerminalCheckbox={intermodalTerminalCheckbox}
                        serviceCenterCheckbox={serviceCenterCheckbox}
                        circuitCapacityCheckbox={circuitCapacityCheckbox}
                        truckParkingLocationCheckbox={truckParkingLocationCheckbox}
                        pemsTrafficCheckbox={pemsTrafficCheckbox}
                        utilitiesCheckbox={utilitiesCheckbox}
                        topParcelPoints={topParcelPoints}
                        landParcelCheckbox={landParcelCheckbox}
                        // landParcelData removed - now using MVT tiles directly
                        onMhdMarkerClick={handleMhdMarkerClick}
                        onTruckStopClick={handleTruckStopClick}
                        onTrafficClick={handleTrafficClick}
                        onCircuitClick={handleCircuitClick}
                        onWarehouseClick={handleWarehouseClick}
                        onUtilityClick={handleUtilityClick}
                        onCalEnviroScreenClick={handleCalEnviroScreenClick}
                        onSraClick={handleSraClick}
                        parcelPointTileUrl={parcelPointTileUrl}
                        parcelPolygonTileUrl={parcelPolygonTileUrl}
                        searchId={searchId}
                        onParcelClick={(properties, coordinate) => {
                            // Convert DeckGL coordinate [lng, lat] to Google Maps event-like object
                            // handlePolygonClick expects (parcel, event) where event has latLng
                            const mockEvent = coordinate && coordinate.length >= 2 ? {
                                latLng: {
                                    lat: () => coordinate[1], // lat is second element
                                    lng: () => coordinate[0]  // lng is first element
                                }
                            } : null;
                            handlePolygonClick(properties, mockEvent);
                        }}
                        highlightedParcelId={highlightedParcelId}
                        currentZoom={currentZoom}
                        parcelFilters={parcelFilters}
                    />
                )}

                {/* Places search markers */}
                {placeResults.map((place) => (
                    <Marker
                        key={place.id}
                        position={place.position}
                        title={place.name}
                        onClick={() => setSelectedPlace(place)}
                    />
                ))}

                {selectedPlace && (
                    <InfoWindow
                        position={selectedPlace.position}
                        onCloseClick={() => setSelectedPlace(null)}
                    >
                        <div className="text-sm">
                            <div className="font-semibold text-gray-800">{selectedPlace.name}</div>
                            {selectedPlace.address && <div className="text-gray-600">{selectedPlace.address}</div>}
                            {selectedPlace.rating && (
                                <div className="text-gray-600">Rating: {selectedPlace.rating}</div>
                            )}
                            {selectedPlace.businessStatus && (
                                <div className="text-gray-600">Status: {selectedPlace.businessStatus}</div>
                            )}
                        </div>
                    </InfoWindow>
                )}

                {/* Render Land Parcels (using old clustering logic) - DISABLED, using vector tiles now */}
                {/* {renderedParcels} */}

                {/* Render Heatmap (still using Google Maps HeatmapLayer) */}
                {renderedHeatmap}

                {/* Info Window for selected parcel or infrastructure */}
                {selectedParcel && infoWindowPosition && (
                    <InfoWindow
                        position={infoWindowPosition}
                        options={{ maxWidth: selectedContentType === 'circuit' ? 380 : 336 }}
                        onCloseClick={closeInfoWindow}
                    >
                        <div className={selectedContentType === 'circuit' ? 'p-2 min-w-[300px]' : 'p-3 max-w-sm'}>
                            <h3 className="font-semibold text-gray-800 mb-2">
                                {selectedContentType === 'circuit'
                                    ? 'Circuit Information'
                                    : selectedContentType === 'mhdInfrastructure'
                                        ? 'Charging Station Information'
                                        : selectedContentType === 'warehouse'
                                            ? 'Warehouse Information'
                                            : selectedContentType === 'traffic'
                                                ? 'Traffic Information'
                                                : selectedContentType === 'truckStop'
                                                    ? 'Truck Stop Information'
                                                    : selectedContentType === 'utility'
                                                        ? 'Utility Information'
                                                        : selectedContentType === 'calEnviroScreen'
                                                            ? 'CalEnviroScreen Tract Information'
                                                        : selectedContentType === 'sra'
                                                            ? 'SRA Information'
                                                        : 'Land Parcel Information'}
                            </h3>
                            <div className={selectedContentType === 'circuit' ? 'text-sm' : 'space-y-1 text-sm'}>
                                <InfoWindowPopup
                                    selectedContent={selectedParcel}
                                    type={selectedContentType}
                                />
                                {selectedContentType === 'landParcel' && (
                                    <div className="pt-2">
                                        <button
                                            type="button"
                                            onClick={handleAddParcelToTable}
                                            disabled={parcelAlreadyInTable}
                                            className={`w-full px-3 py-2 rounded-md text-sm font-semibold transition-colors ${
                                                parcelAlreadyInTable
                                                    ? 'bg-green-600 text-white cursor-default'
                                                    : 'bg-blue-600 text-white hover:bg-blue-700'
                                            }`}
                                        >
                                            {parcelAlreadyInTable ? '✓ Added to Table' : '+ Add to Comparison Table'}
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>
                    </InfoWindow>
                )}
            </GoogleMap>
            {/* Places text search control */}
            <div className="absolute top-2 right-16 z-20">
                <div className="bg-white shadow-lg rounded-lg border border-gray-200 p-3 flex items-center w-[360px]">
                    <input
                        ref={autocompleteInputRef}
                        id="pac-input"
                        type="text"
                        value={placeQuery}
                        onChange={(e) => setPlaceQuery(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                                handlePlacesSearch();
                            }
                        }}
                        placeholder="Search for places of interest..."
                        className="flex-1 min-w-0 border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-900"
                    />
                    <button
                        type="button"
                        onClick={handlePlacesSearch}
                        className="ml-2 bg-blue-600 text-white px-3 py-2 rounded-md text-sm font-medium hover:bg-blue-700"
                    >
                        Go
                    </button>
                </div>
            </div>
            {/* Search in this area button */}
            {handleSearchInArea && (
                <div className="absolute top-16 left-4 z-10">
                    <button
                        onClick={handleSearchInArea}
                        disabled={loading}
                        className="bg-blue-600 text-white px-4 py-2 rounded-lg shadow-lg flex items-center gap-2 text-sm font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                        {loading ? (
                            <>
                                <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                                Loading...
                            </>
                        ) : (
                            <>
                                {/* <FaSearch className="w-4 h-4" /> */}
                                <IoMdRefresh className="w-4 h-4" />
                                Refresh parcels
                            </>
                        )}
                    </button>
                </div>
            )}
            {(currentZoom < 13 && circuitCapacityCheckbox) && (<div className="absolute bottom-4 left-1/2 -translate-x-1/2">
                <div className="bg-white text-gray-600 px-4 py-2 rounded-lg shadow-lg flex items-center gap-2 text-sm border">
                    <FiInfo className="w-4 h-4" /> Zoom in closer to view circuit lines
                </div>
            </div>)}
            {/* Auto-fetch indicator - shows when data is being loaded automatically */}
            {/* {loading && (
                <div className="absolute top-4 left-1/2 -translate-x-1/2">
                    <div className="bg-white text-gray-600 px-4 py-2 rounded-lg shadow-lg flex items-center gap-2 text-sm border">
                        <div className="animate-spin rounded-full h-4 w-4 border-2 border-blue-600 border-t-transparent"></div>
                        Loading parcels for this area...
                    </div>
                </div>
            )} */}
            {/* {process.env.NODE_ENV !== 'production' && (
                <div className="absolute bottom-4 left-1/2 -translate-x-1/2">
                    <div className="bg-white text-gray-600 px-4 py-2 rounded-lg shadow-lg flex items-center gap-2 text-sm border">
                        Current zoom level: {currentZoom}
                    </div>
                </div>
            )} */}
        </div>
            {/* Tabbed Legend System */}
            <div className="bg-white rounded-lg shadow-lg border border-gray-200 p-3 max-w-4xl text-xs mt-1 flex-shrink-0">
                <div className="flex flex-row gap-2">
                    <span className="font-semibold mt-0.5 text-sm">Legend:</span>
                    {/* Legend Tabs */}
                    <div className="flex gap-2 mb-3">
                        <button
                            onClick={() => setActiveLegendTab('circuits')}
                            className={`px-3 py-1 rounded-md font-medium transition-colors ${activeLegendTab === 'circuits'
                                ? 'bg-blue-500 text-white'
                                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                                }`}
                        >
                            Power Grid Capacity
                        </button>
                        <button
                            onClick={() => setActiveLegendTab('traffic')}
                            className={`px-3 py-1 rounded-md font-medium transition-colors ${activeLegendTab === 'traffic'
                                ? 'bg-blue-500 text-white'
                                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                                }`}
                        >
                            Traffic Volume
                        </button>
                        <button
                            onClick={() => setActiveLegendTab('truckHeatmap')}
                            className={`px-3 py-1 rounded-md font-medium transition-colors ${activeLegendTab === 'truckHeatmap'
                                ? 'bg-blue-500 text-white'
                                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                                }`}
                        >
                            Truck Hotspots
                        </button>
                    </div>
                </div>

                {/* Legend Content */}
                <div className="">
                    {activeLegendTab === 'circuits' && (
                        <div className="flex flex-col gap-2">
                            <div className="mb-1 font-medium">Power grid capacity:</div>
                            <div className="grid grid-cols-4">
                                {CIRCUIT_CAPACITY_BUCKETS.map((bucket, index) => (
                                    <div key={bucket.label} className="flex flex-col items-center min-w-[70px]">
                                        <div
                                            className={`w-full h-2 ${index === 0 ? 'rounded-l' : ''} ${index === CIRCUIT_CAPACITY_BUCKETS.length - 1 ? 'rounded-r' : ''}`}
                                            style={{ backgroundColor: bucket.color }}
                                        ></div>
                                        <div className="text-xs mt-1 text-gray-600">{bucket.label}</div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {activeLegendTab === 'traffic' && (
                        <div className="flex flex-col">
                            <div className="mb-1 font-medium">Traffic Volume (trucks/day):</div>
                            <div className="flex flex-row flex-wrap">
                                <div className="flex flex-col items-center">
                                    <div className="w-20 h-2 bg-[#006400] rounded-l"></div>
                                    <div className="text-xs mt-1">0-1,000</div>
                                </div>
                                <div className="flex flex-col items-center">
                                    <div className="w-20 h-2 bg-[#00FF00]"></div>
                                    <div className="text-xs mt-1">1,000-2,000</div>
                                </div>
                                <div className="flex flex-col items-center">
                                    <div className="w-20 h-2 bg-[#FFFF00]"></div>
                                    <div className="text-xs mt-1">2,000-3,000</div>
                                </div>
                                <div className="flex flex-col items-center">
                                    <div className="w-20 h-2 bg-[#FFA500]"></div>
                                    <div className="text-xs mt-1">3,000-4,000</div>
                                </div>
                                <div className="flex flex-col items-center">
                                    <div className="w-20 h-2 bg-[#FF8C00]"></div>
                                    <div className="text-xs mt-1">4,000-5,000</div>
                                </div>
                                <div className="flex flex-col items-center">
                                    <div className="w-20 h-2 bg-[#FF4500]"></div>
                                    <div className="text-xs mt-1">5,000-6,000</div>
                                </div>
                                <div className="flex flex-col items-center">
                                    <div className="w-20 h-2 bg-[#FF0000] rounded-r"></div>
                                    <div className="text-xs mt-1">6,000+</div>
                                </div>
                            </div>
                        </div>
                    )}

                    {activeLegendTab === 'truckHeatmap' && (
                        <div className="flex flex-col">
                            <div className="mb-1 font-medium">Truck Parking Intensity:</div>
                            <div className="flex flex-row">
                                {[
                                    ["#FFEDA0", "Low", "rounded-l"],
                                    ["#FED976", "", ""],
                                    ["#FEB24C", "Medium", ""],
                                    ["#FD8D3C", "", ""],
                                    ["#FC4E2A", "High", ""],
                                    ["#E31A1C", "", ""],
                                    ["#BD0026", "Very High", ""],
                                    ["#800026", "", "rounded-r"],
                                ].map(([color, label, radius]) => (
                                    <div key={color} className="flex flex-col items-center">
                                        <div className={`w-16 h-2 ${radius}`} style={{ backgroundColor: color }} />
                                        {label ? <div className="text-xs mt-1">{label}</div> : null}
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
});